/**
 * CreditAuditLogs - Composant pour afficher les logs d'audit des attributions de crédits
 * Super Admin Dashboard
 */
import React, { useState, useEffect, useMemo } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Input } from './ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { DatePickerFr } from './ui/DatePickerFr';
import { FileText, Download, Search, RefreshCw, Calendar, TrendingUp, Users, Shield, Clock, ChevronLeft, ChevronRight, ArrowUpCircle, ArrowDownCircle, Gift, Zap, AlertTriangle } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;

// Configuration des actions
const ACTION_CONFIG = {
  ATTRIBUTION: { label: 'Attribution', color: 'bg-green-100 text-green-800', icon: ArrowUpCircle },
  RETRAIT: { label: 'Retrait', color: 'bg-red-100 text-red-800', icon: ArrowDownCircle },
  AJUSTEMENT: { label: 'Ajustement', color: 'bg-yellow-100 text-yellow-800', icon: RefreshCw },
  BONUS_MANUEL: { label: 'Bonus Manuel', color: 'bg-purple-100 text-purple-800', icon: Gift },
  REMBOURSEMENT: { label: 'Remboursement', color: 'bg-blue-100 text-blue-800', icon: Zap },
};

// Configuration des types de crédit
const TYPE_CREDIT_CONFIG = {
  STANDARD: { label: 'Standard', color: 'bg-blue-100 text-blue-800' },
  PRIORITY: { label: 'Prioritaire', color: 'bg-orange-100 text-orange-800' },
  BONUS: { label: 'Bonus', color: 'bg-green-100 text-green-800' },
  TEMPORARY: { label: 'Temporaire', color: 'bg-purple-100 text-purple-800' },
};

export default function CreditAuditLogs({ getAuthHeader }) {
  const [logs, setLogs] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [pagination, setPagination] = useState({ total: 0, total_pages: 1 });
  
  // Filtres
  const [searchTerm, setSearchTerm] = useState('');
  const [filterAction, setFilterAction] = useState('all');
  const [filterDateDebut, setFilterDateDebut] = useState('');
  const [filterDateFin, setFilterDateFin] = useState('');

  const fetchLogs = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ page, limit: 20 });
      if (filterAction !== 'all') params.append('action', filterAction);
      if (filterDateDebut) params.append('date_debut', filterDateDebut);
      if (filterDateFin) params.append('date_fin', filterDateFin);
      
      const response = await axios.get(`${API}/api/credits/audit-logs?${params}`, {
        headers: getAuthHeader()
      });
      
      setLogs(response.data.logs || []);
      setPagination(response.data.pagination || { total: 0, total_pages: 1 });
    } catch {
      toast.error('Erreur lors du chargement des logs');
    } finally {
      setLoading(false);
    }
  };

  const fetchStats = async () => {
    try {
      const response = await axios.get(`${API}/api/credits/audit-logs/stats`, {
        headers: getAuthHeader()
      });
      setStats(response.data.stats);
    } catch {
    }
  };

  useEffect(() => {
    fetchLogs();
    fetchStats();
  }, [page, filterAction, filterDateDebut, filterDateFin]);

  const handleExport = async (format) => {
    try {
      const params = new URLSearchParams({ format });
      if (filterDateDebut) params.append('date_debut', filterDateDebut);
      if (filterDateFin) params.append('date_fin', filterDateFin);
      
      if (format === 'csv') {
        const response = await axios.get(`${API}/api/credits/audit-logs/export?${params}`, {
          headers: getAuthHeader(),
          responseType: 'blob'
        });
        
        const url = window.URL.createObjectURL(new Blob([response.data]));
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', `audit_credits_${new Date().toISOString().split('T')[0]}.csv`);
        document.body.appendChild(link);
        link.click();
        link.remove();
        toast.success('Export CSV téléchargé');
      } else {
        const response = await axios.get(`${API}/api/credits/audit-logs/export?${params}`, {
          headers: getAuthHeader()
        });
        
        const blob = new Blob([JSON.stringify(response.data.logs, null, 2)], { type: 'application/json' });
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', `audit_credits_${new Date().toISOString().split('T')[0]}.json`);
        document.body.appendChild(link);
        link.click();
        link.remove();
        toast.success('Export JSON téléchargé');
      }
    } catch {
      toast.error('Erreur lors de l\'export');
    }
  };

  // Filtrage local par recherche
  const filteredLogs = useMemo(() => {
    if (!searchTerm) return logs;
    const term = searchTerm.toLowerCase();
    return logs.filter(log => 
      (log.admin_email?.toLowerCase() || '').includes(term) ||
      (log.admin_nom?.toLowerCase() || '').includes(term) ||
      (log.target_user_email?.toLowerCase() || '').includes(term) ||
      (log.target_user_nom?.toLowerCase() || '').includes(term) ||
      (log.raison?.toLowerCase() || '').includes(term)
    );
  }, [logs, searchTerm]);

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    const date = new Date(dateStr);
    return date.toLocaleString('fr-FR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getActionBadge = (action) => {
    const config = ACTION_CONFIG[action] || { label: action, color: 'bg-gray-100 text-gray-800' };
    const Icon = config.icon || FileText;
    return (
      <Badge className={`${config.color} flex items-center gap-1`}>
        <Icon className="h-3 w-3" />
        {config.label}
      </Badge>
    );
  };

  const getTypeCreditBadge = (type) => {
    const config = TYPE_CREDIT_CONFIG[type] || { label: type, color: 'bg-gray-100 text-gray-800' };
    return <Badge className={config.color}>{config.label}</Badge>;
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <Shield className="h-7 w-7 text-blue-600" />
            Audit Log - Attributions Crédits
          </h2>
          <p className="text-gray-500 mt-1">Traçabilité complète des opérations sur les crédits</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={() => handleExport('csv')} size="sm">
            <Download className="h-4 w-4 mr-2" />
            Export CSV
          </Button>
          <Button variant="outline" onClick={() => handleExport('json')} size="sm">
            <Download className="h-4 w-4 mr-2" />
            Export JSON
          </Button>
          <Button onClick={() => { fetchLogs(); fetchStats(); }} size="sm">
            <RefreshCw className="h-4 w-4 mr-2" />
            Actualiser
          </Button>
        </div>
      </div>

      {/* Statistiques */}
      {stats && (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <Card className="bg-gradient-to-br from-blue-50 to-white border-blue-200">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-blue-600 font-medium">Total Opérations</p>
                  <p className="text-3xl font-bold text-blue-700">{stats.total_operations || 0}</p>
                </div>
                <FileText className="h-10 w-10 text-blue-400" />
              </div>
            </CardContent>
          </Card>
          
          <Card className="bg-gradient-to-br from-green-50 to-white border-green-200">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-green-600 font-medium">Crédits Attribués</p>
                  <p className="text-3xl font-bold text-green-700">+{stats.total_credits_attribues || 0}</p>
                </div>
                <TrendingUp className="h-10 w-10 text-green-400" />
              </div>
            </CardContent>
          </Card>
          
          <Card className="bg-gradient-to-br from-purple-50 to-white border-purple-200">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-purple-600 font-medium">Admins Actifs</p>
                  <p className="text-3xl font-bold text-purple-700">{stats.par_admin?.length || 0}</p>
                </div>
                <Users className="h-10 w-10 text-purple-400" />
              </div>
            </CardContent>
          </Card>
          
          <Card className="bg-gradient-to-br from-orange-50 to-white border-orange-200">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-orange-600 font-medium">7 Derniers Jours</p>
                  <p className="text-3xl font-bold text-orange-700">
                    {stats.derniers_7_jours?.reduce((sum, d) => sum + d.count, 0) || 0}
                  </p>
                </div>
                <Calendar className="h-10 w-10 text-orange-400" />
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Filtres */}
      <Card>
        <CardContent className="pt-6">
          <div className="flex flex-col md:flex-row gap-4">
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
              <Input
                placeholder="Rechercher par admin, utilisateur, raison..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10"
              />
            </div>
            
            <Select value={filterAction} onValueChange={setFilterAction}>
              <SelectTrigger className="w-[180px]">
                <SelectValue placeholder="Type d'action" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Toutes les actions</SelectItem>
                <SelectItem value="ATTRIBUTION">Attribution</SelectItem>
                <SelectItem value="RETRAIT">Retrait</SelectItem>
                <SelectItem value="AJUSTEMENT">Ajustement</SelectItem>
                <SelectItem value="BONUS_MANUEL">Bonus Manuel</SelectItem>
                <SelectItem value="REMBOURSEMENT">Remboursement</SelectItem>
              </SelectContent>
            </Select>
            
            <DatePickerFr
              className="w-[160px]"
              value={filterDateDebut}
              onChange={setFilterDateDebut}
              placeholder="Date début"
            />
            
            <DatePickerFr
              className="w-[160px]"
              value={filterDateFin}
              onChange={setFilterDateFin}
              placeholder="Date fin"
            />
          </div>
        </CardContent>
      </Card>

      {/* Table des logs */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center justify-between">
            <span>Historique des opérations ({pagination.total})</span>
          </CardTitle>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <RefreshCw className="h-8 w-8 animate-spin text-blue-600" />
            </div>
          ) : filteredLogs.length === 0 ? (
            <div className="text-center py-12 text-gray-500">
              <AlertTriangle className="h-12 w-12 mx-auto mb-4 text-gray-400" />
              <p>Aucun log d'audit trouvé</p>
            </div>
          ) : (
            <>
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b bg-gray-50">
                      <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600">Date</th>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600">Action</th>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600">Admin</th>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600">Utilisateur</th>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600">Type</th>
                      <th className="px-4 py-3 text-right text-xs font-semibold text-gray-600">Crédits</th>
                      <th className="px-4 py-3 text-right text-xs font-semibold text-gray-600">Solde Après</th>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600">Raison</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y">
                    {filteredLogs.map((log) => (
                      <tr key={log.id} className="hover:bg-gray-50">
                        <td className="px-4 py-3 text-sm text-gray-600">
                          <div className="flex items-center gap-1">
                            <Clock className="h-3 w-3" />
                            {formatDate(log.created_at)}
                          </div>
                        </td>
                        <td className="px-4 py-3">
                          {getActionBadge(log.action)}
                        </td>
                        <td className="px-4 py-3">
                          <div>
                            <p className="text-sm font-medium text-gray-900">{log.admin_nom || '-'}</p>
                            <p className="text-xs text-gray-500">{log.admin_email || '-'}</p>
                          </div>
                        </td>
                        <td className="px-4 py-3">
                          <div>
                            <p className="text-sm font-medium text-gray-900">{log.target_user_nom || '-'}</p>
                            <p className="text-xs text-gray-500">{log.target_user_email || '-'}</p>
                          </div>
                        </td>
                        <td className="px-4 py-3">
                          {getTypeCreditBadge(log.type_credit)}
                        </td>
                        <td className="px-4 py-3 text-right">
                          <span className={`font-bold ${log.credits_modifies >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                            {log.credits_modifies >= 0 ? '+' : ''}{log.credits_modifies}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-right font-medium text-gray-900">
                          {log.credits_apres}
                        </td>
                        <td className="px-4 py-3">
                          <p className="text-sm text-gray-600 max-w-[200px] truncate" title={log.raison}>
                            {log.raison || '-'}
                          </p>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Pagination */}
              {pagination.total_pages > 1 && (
                <div className="flex items-center justify-between mt-4 pt-4 border-t">
                  <p className="text-sm text-gray-500">
                    Page {page} sur {pagination.total_pages} ({pagination.total} résultats)
                  </p>
                  <div className="flex gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setPage(p => Math.max(1, p - 1))}
                      disabled={page === 1}
                    >
                      <ChevronLeft className="h-4 w-4" />
                      Précédent
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setPage(p => Math.min(pagination.total_pages, p + 1))}
                      disabled={page === pagination.total_pages}
                    >
                      Suivant
                      <ChevronRight className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              )}
            </>
          )}
        </CardContent>
      </Card>

      {/* Top Admins */}
      {stats?.par_admin?.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Users className="h-5 w-5 text-purple-600" />
              Top Admins - Attributions
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {stats.par_admin.slice(0, 6).map((admin, index) => (
                <div key={index} className="p-4 bg-gray-50 rounded-lg flex items-center justify-between">
                  <div>
                    <p className="font-medium text-gray-900">{admin.admin_nom || 'N/A'}</p>
                    <p className="text-xs text-gray-500">{admin.admin_email || '-'}</p>
                  </div>
                  <div className="text-right">
                    <p className="text-lg font-bold text-purple-700">{admin.count}</p>
                    <p className="text-xs text-gray-500">opérations</p>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
